import React, { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Search,
  Plus,
  Edit,
  Trash2,
  RefreshCw,
  ChevronUp,
  ChevronDown
} from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import toast from 'react-hot-toast'
import { EntityEditForm } from '@/components/EntityEditForm'
import { ConfirmationModal } from '@/components/monitoring/ConfirmationModal'
import { useAuth } from '@/hooks/useAuth'

// Types
interface BrokerFilters {
  search: string
  page: number
  limit: number
  sortBy: string
  sortOrder: 'asc' | 'desc'
}

export function Brokers() {
  const [filters, setFilters] = useState<BrokerFilters>({
    search: '',
    page: 1,
    limit: 50,
    sortBy: 'name',
    sortOrder: 'desc'
  })
  const [selectedBrokers, setSelectedBrokers] = useState<number[]>([])
  
  const [editingBroker, setEditingBroker] = useState<number | null>(null)
  const [creatingBroker, setCreatingBroker] = useState(false)
  const [deletingBroker, setDeletingBroker] = useState<
    { id: number; name: string } | null
  >(null)

  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { canMutate } = useAuth()
  const rowColSpan = canMutate ? 9 : 8

  // Sortable header component
  const SortableHeader = ({ column, children, className = "" }: { column: string; children: React.ReactNode; className?: string }) => {
    const isActive = filters.sortBy === column
    const isAsc = isActive && filters.sortOrder === 'asc'
    const isDesc = isActive && filters.sortOrder === 'desc'

    return (
      <th 
        className={`px-6 py-3 text-xs font-medium text-secondary-500 uppercase tracking-wider cursor-pointer hover:bg-secondary-100 select-none ${className}`}
        onClick={() => handleSort(column)}
      >
        <div className="flex items-center space-x-1">
          <span>{children}</span>
          <div className="flex flex-col">
            <ChevronUp 
              className={`h-3 w-3 ${isAsc ? 'text-primary-600' : 'text-secondary-400'}`} 
            />
            <ChevronDown 
              className={`h-3 w-3 -mt-1 ${isDesc ? 'text-primary-600' : 'text-secondary-400'}`} 
            />
          </div>
        </div>
      </th>
    )
  }

  // Fetch brokers with filtering and pagination
  const { data: brokersResponse, isLoading, error, refetch } = useQuery({
    queryKey: ['brokers', filters],
    queryFn: async () => {
      try {
        console.log('Fetching brokers with filters:', filters);
        const params = new URLSearchParams()
        params.append('page', filters.page.toString())
        params.append('limit', filters.limit.toString())
        if (filters.search) params.append('search', filters.search)
        if (filters.sortBy) params.append('sortBy', filters.sortBy)
        if (filters.sortOrder) params.append('sortOrder', filters.sortOrder)

        const response = await api.get(`/brokers?${params}`)
        console.log('Brokers response:', response.data);

        return response.data
      } catch (error) {
        console.error('Error fetching brokers:', error);
        throw error;
      }
    },
    retry: 3,
    retryDelay: 1000,
  })

  const brokers = brokersResponse?.data || []
  const totalBrokers = brokersResponse?.meta?.total || 0
  const totalPages = Math.ceil(totalBrokers / filters.limit)

  // Process brokers data
  const processedBrokers = useMemo(() => {
    return brokers.map((broker: any) => ({
      ...broker,
      name: broker.name || 'Unnamed Broker',
      nr_projects: Number(broker?.nr_projects ?? 0),
      nr_units: Number(broker?.nr_units ?? 0),
      total_m2: Number(broker?.total_m2 ?? 0),
      nr_units_available: Number(broker?.nr_units_available ?? 0),
      total_m2_available: Number(broker?.total_m2_available ?? 0),
    }))
  }, [brokers])

  // Bulk delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (brokerIds: number[]) => {
      const deletePromises = brokerIds.map(id => api.delete(`/brokers/${id}`))
      await Promise.all(deletePromises)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brokers'] })
      setSelectedBrokers([])
      toast.success('Brokers deleted successfully')
    },
    onError: (error) => {
      console.error('Error deleting brokers:', error)
      toast.error('Failed to delete brokers')
    }
  })

  // Single delete mutation
  const singleDeleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/brokers/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brokers'] })
      queryClient.invalidateQueries({ queryKey: ['/brokers-dropdown'] })
      toast.success('Broker deleted successfully')
      setDeletingBroker(null)
    },
    onError: (error: any) => {
      console.error('Error deleting broker:', error)
      const status = error?.response?.status
      const backendMessage = error?.response?.data?.message
      if (status === 409 || /foreign key|constraint/i.test(String(backendMessage))) {
        toast.error('Cannot delete: this broker still has associated projects. Reassign them first.')
      } else {
        toast.error(
          Array.isArray(backendMessage) ? backendMessage[0] : backendMessage || 'Failed to delete broker'
        )
      }
    }
  })

  // Handle search
  const handleSearch = (searchTerm: string) => {
    setFilters(prev => ({ ...prev, search: searchTerm, page: 1 }))
  }

  // Handle sorting
  const handleSort = (column: string) => {
    setFilters(prev => ({
      ...prev,
      sortBy: column,
      sortOrder: prev.sortBy === column && prev.sortOrder === 'desc' ? 'asc' : 'desc',
      page: 1
    }))
  }

  // Handle pagination
  const handlePageChange = (newPage: number) => {
    setFilters(prev => ({ ...prev, page: newPage }))
  }

  // Handle bulk delete
  const handleBulkDelete = () => {
    if (selectedBrokers.length === 0) {
      toast.error('Please select brokers to delete')
      return
    }

    if (window.confirm(`Are you sure you want to delete ${selectedBrokers.length} brokers?`)) {
      deleteMutation.mutate(selectedBrokers)
    }
  }

  // Handle single delete — opens confirmation modal after pre-validating FK usage
  const handleDeleteBroker = (
    broker: { broker_id: number; name: string; nr_projects: number }
  ) => {
    if (broker.nr_projects > 0) {
      toast.error(
        `Cannot delete "${broker.name}": it has ${broker.nr_projects} associated project${
          broker.nr_projects === 1 ? '' : 's'
        }. Reassign or delete those projects first.`
      )
      return
    }
    setDeletingBroker({ id: broker.broker_id, name: broker.name })
  }

  // Handle select all
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedBrokers(processedBrokers.map(broker => broker.broker_id))
    } else {
      setSelectedBrokers([])
    }
  }

  // Handle select individual
  const handleSelectBroker = (id: number, checked: boolean) => {
    if (checked) {
      setSelectedBrokers(prev => [...prev, id])
    } else {
      setSelectedBrokers(prev => prev.filter(brokerId => brokerId !== id))
    }
  }

  const handleRowClick = (brokerId: number) => {
    navigate(`/brokers/${brokerId}`)
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-danger-600 mb-2">Error loading brokers</div>
          <button
            onClick={() => refetch()}
            className="btn-primary"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6 pt-2">
      <div className="bg-white rounded-xl shadow-tagus p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
              <input
                type="text"
                placeholder="Search brokers..."
                value={filters.search}
                onChange={(e) => handleSearch(e.target.value)}
                className="form-input pl-10"
              />
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <button
              onClick={() => setCreatingBroker(true)}
              className="btn-primary flex items-center"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Broker
            </button>
            {selectedBrokers.length > 0 && (
              <button
                onClick={handleBulkDelete}
                disabled={deleteMutation.isPending}
                className="btn-danger flex items-center"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete ({selectedBrokers.length})
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-tagus overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-[1000px] w-full divide-y divide-secondary-200 table-fixed">
            <thead className="bg-secondary-50 sticky top-0 z-10">
              <tr>
                <th className="w-16 px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedBrokers.length === processedBrokers.length && processedBrokers.length > 0}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                  />
                </th>
                <SortableHeader column="name" className="text-left">
                  Name
                </SortableHeader>
                <SortableHeader column="nr_projects" className="w-36 text-right">
                  Number Projects
                </SortableHeader>
                <SortableHeader column="nr_units" className="w-32 text-right">
                  Number Units
                </SortableHeader>
                <SortableHeader column="total_m2" className="w-32 text-right">
                  Total M2
                </SortableHeader>
                <SortableHeader column="nr_units_available" className="w-36 text-right">
                  Units Available
                </SortableHeader>
                <SortableHeader column="total_m2_available" className="w-32 text-right">
                  M2 Available
                </SortableHeader>
                <SortableHeader column="created_at" className="w-32 text-left">
                  Created
                </SortableHeader>
                {canMutate && (
                  <th className="w-24 px-6 py-3 text-center text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-secondary-200">
              {isLoading ? (
                <tr>
                  <td colSpan={rowColSpan} className="px-6 py-4 text-center">
                    <div className="flex items-center justify-center">
                      <RefreshCw className="h-5 w-5 animate-spin text-primary-600 mr-2" />
                      Loading brokers...
                    </div>
                  </td>
                </tr>
              ) : processedBrokers.length === 0 ? (
                <tr>
                  <td colSpan={rowColSpan} className="px-6 py-4 text-center text-secondary-500">
                    No brokers found
                  </td>
                </tr>
              ) : (
                processedBrokers.map((broker) => (
                <tr
                  key={broker.broker_id}
                  className="hover:bg-secondary-50 cursor-pointer"
                  onClick={() => handleRowClick(broker.broker_id)}
                >
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedBrokers.includes(broker.broker_id)}
                        onClick={(event) => event.stopPropagation()}
                        onChange={(e) => handleSelectBroker(broker.broker_id, e.target.checked)}
                        className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                      />
                    </td>
                    <td className="px-6 py-4 truncate max-w-[200px] sm:max-w-xs md:max-w-sm lg:max-w-md xl:max-w-lg">
                      <div className="text-sm font-medium text-secondary-900 truncate" title={broker.name}>
                        {broker.name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-secondary-900">
                      {broker.nr_projects.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-secondary-900">
                      {broker.nr_units.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-secondary-900">
                      {broker.total_m2.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-secondary-900">
                      {broker.nr_units_available.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-secondary-900">
                      {broker.total_m2_available.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-500">
                      {new Date(broker.created_at).toLocaleDateString()}
                    </td>
                    {canMutate && (
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation()
                            handleDeleteBroker(broker)
                          }}
                          className="inline-flex items-center justify-center p-1.5 rounded-md text-danger-600 hover:bg-danger-50 hover:text-danger-700 focus:outline-none focus:ring-2 focus:ring-danger-500"
                          title={`Delete ${broker.name}`}
                          aria-label={`Delete ${broker.name}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-secondary-700">
            Showing {((filters.page - 1) * filters.limit) + 1} to {Math.min(filters.page * filters.limit, totalBrokers)} of {totalBrokers} results
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => handlePageChange(filters.page - 1)}
              disabled={filters.page === 1}
              className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="px-3 py-2 text-sm text-secondary-700">
              Page {filters.page} of {totalPages}
            </span>
            <button
              onClick={() => handlePageChange(filters.page + 1)}
              disabled={filters.page === totalPages}
              className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {(creatingBroker || editingBroker) && (
        <EntityEditForm
          type="broker"
          entityId={editingBroker}
          onClose={() => {
            setCreatingBroker(false)
            setEditingBroker(null)
          }}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['brokers'] })
            setCreatingBroker(false)
            setEditingBroker(null)
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deletingBroker !== null}
        onClose={() => setDeletingBroker(null)}
        onConfirm={() => {
          if (deletingBroker) singleDeleteMutation.mutate(deletingBroker.id)
        }}
        title="Delete Broker"
        message={
          deletingBroker
            ? `Are you sure you want to permanently delete "${deletingBroker.name}"? This action cannot be undone.`
            : ''
        }
        confirmLabel="Delete"
        confirmVariant="danger"
        isLoading={singleDeleteMutation.isPending}
      />
    </div>
  )
}
